import { useCallback, useEffect, useRef, useState } from "react";
import { listClientLetters, listLetterTemplates } from "@/features/letters/api";
import type { Letter, LetterTemplate } from "@/features/letters/types";
import { ApiError } from "@/lib/api";

interface UseClientLettersResult {
  templates: LetterTemplate[];
  letters: Letter[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Loads letter templates + the client's letters together; race guards match use-cases.ts. */
export function useClientLetters(clientId: string): UseClientLettersResult {
  const [templates, setTemplates] = useState<LetterTemplate[]>([]);
  const [letters, setLetters] = useState<Letter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${clientId}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    Promise.all([listLetterTemplates(), listClientLetters(clientId)])
      .then(([tpls, ltrs]) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setTemplates(tpls);
        setLetters(ltrs);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setTemplates([]);
        setLetters([]);
        setError(err instanceof ApiError ? err.message : "Failed to load letters.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [clientId, reloadKey]);

  return { templates, letters, loading, error, refetch };
}
